<template>
    <v-card
        v-if="should_show_message"
        class="creditor-unverified-kyc-message-box pa-6"
        outlined
    >
        <div class="kyc-message-wrapper">
            <div class="kyc-message-content">
                <div class="body-1 kyc-message-text">
                    <p>
                        <strong> {{ sanitized_message_heading }} </strong>
                    </p>
                    <p>
                        {{ sanitized_message_body }}
                    </p>
                    <p>
                        {{ sanitized_message_email_contact }}
                        <a :href="`mailto:${kyc_contact_email}`">{{ kyc_contact_email }}</a>.
                    </p>
                </div>

                <v-btn
                    @click="navigate_to_kyc_form"
                    class="mt-4"
                    color="primary"
                    dark
                >
                    {{ translate('common.fill_kyc_details') }}
                    <v-icon
                        right
                        small
                    >
                        mdi-arrow-right
                    </v-icon>
                </v-btn>
            </div>
        </div>
    </v-card>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { item_mixin } from '@/mixins/item'
import { html_mixin } from '@/mixins/html'

export default {
    name: 'TheDashboardCreditorUnverifiedKycMessage',
    props: {
        message: {
            type: String,
            default: null,
        },
    },
    mixins: [item_mixin, html_mixin],
    components: {},
    data() {
        return {
            creditor: null,
        }
    },
    computed: {
        // Get creditor_id only from user_extended_data.creditor
        effective_creditor_id() {
            return this.user_extended_data?.creditor || null
        },
        is_creditor_user() {
            // Check if user is a creditor-type user (not solicitor, not system user)
            // and has a creditor in user_extended_data
            if (this.solicitor || this.system_user) {
                return false
            }
            return !!this.effective_creditor_id
        },
        kyc_verified() {
            // Get kyc_verified from the creditor object
            return this.creditor?.kyc_verified
        },
        should_show_message() {
            // Only show if user is a creditor-type user AND creditor has kyc_verified as false
            return this.is_creditor_user && this.kyc_verified === false
        },
        kyc_form_url() {
            // Get KYC form url from environment variable
            return process.env.VUE_APP_KYC_FORM_URL || null
        },
        kyc_contact_email() {
            // Get KYC contact email from environment variable
            return process.env.VUE_APP_KYC_CONTACT_EMAIL || null
        },
        sanitized_message_heading() {
            // Simply sanitize the HTML message without any formatting
            const html_message = this.message || this.translate('common.creditor_unverified_kyc_message.heading')
            return this.html_sanitize(html_message)
        },
        sanitized_message_body() {
            // Simply sanitize the HTML message without any formatting
            const html_message = this.message || this.translate('common.creditor_unverified_kyc_message.body')
            return this.html_sanitize(html_message)
        },
        sanitized_message_email_contact() {
            // Simply sanitize the HTML message without any formatting
            const html_message = this.message || this.translate('common.creditor_unverified_kyc_message.email_contact')
            return this.html_sanitize(html_message)
        },
        ...mapGetters(['solicitor', 'system_user']),
        ...mapState(['user_extended_data']),
    },
    watch: {
        effective_creditor_id: {
            async handler(creditor_id) {
                if (creditor_id) {
                    await this.get_creditor()
                } else {
                    this.creditor = null
                }
            },
            immediate: true,
        },
    },
    methods: {
        async get_creditor() {
            if (!this.effective_creditor_id) return

            try {
                const response = await this.api_get({
                    url: `/creditors/${this.effective_creditor_id}`,
                })
                this.creditor = response.data
            } catch (error) {
                console.error('Error fetching creditor:', error)
                this.creditor = null
            } finally {}
        },
        navigate_to_kyc_form() {
            // Open external KYC form URL
            const url = this.kyc_form_url
            if (!url) {
                console.error('KYC form URL not configured environment variables')
                this.push_state_property({
                    state_property: 'snackbar_messages',
                    data: {
                        text: 'common.something_went_wrong',
                    },
                })

                return
            }
            
            // Try to open the URL and check if it succeeded
            try {
                const newWindow = window.open(url, '_blank')
                
                // window.open returns null if popup was blocked or failed
                if (!newWindow) {
                    this.push_state_property({
                        state_property: 'snackbar_messages',
                        data: {
                            text: 'common.something_went_wrong',
                        },
                    })
                    return
                }
                
                // Check if the window was closed immediately (might indicate invalid URL)
                // Note: This is a best-effort check, as we can't reliably detect all URL failures
                setTimeout(() => {
                    if (newWindow.closed) {
                        this.push_state_property({
                            state_property: 'snackbar_messages',
                            data: {
                                text: 'common.something_went_wrong',
                            },
                        })
                    }
                }, 1000)
            } catch (error) {
                // Catch any errors when trying to open the URL
                console.error('Error opening KYC form URL:', error)
                this.push_state_property({
                    state_property: 'snackbar_messages',
                    data: {
                        text: 'common.something_went_wrong',
                    },
                })
            }
        },
        ...mapActions(['push_state_property']),
    },
}
</script>
<style lang="scss" scoped>
.creditor-unverified-kyc-message-box {
    position: relative;
    background-color: #f5f5f5 !important; // Light gray tint
}

.kyc-message-wrapper {
    position: relative;
}

.kyc-message-content {

    .kyc-message-text {
        color: rgba(0, 0, 0, 0.87);
        line-height: 1.5;
        
        p {
            margin-bottom: 12px;
            
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}
</style>

